import React, { useState, forwardRef, useImperativeHandle, ForwardRefRenderFunction, useRef, useCallback } from 'react';
import { View, Platform, TouchableOpacity, StyleSheet, Pressable, type ViewInstance } from 'react-native';

// Conditionally import Picker only on native platforms
let Picker: any = null;
if (Platform.OS !== 'web') {
	try {
		const PickerModule = require('@react-native-picker/picker');
		Picker = PickerModule.Picker;
	} catch (error) {
		console.warn('Failed to load @react-native-picker/picker:', error);
	}
}

import { Text, Icon, Portal, Modal, useTheme, Button } from '@/components/ui';
import type { IconName } from '@/types/icons';

export interface PickerOption<T extends string> {
	value: T;
	label: string;
	icon?: IconName;
}

export interface PickerInputProps<T extends string> {
	value: T;
	onChange: (value: T) => void;
	options: PickerOption<T>[];
	label?: string;
	placeholder?: string;
	renderSelectedOption?: (option: PickerOption<T>) => React.ReactNode;
	onSubmitEditing?: () => void;
	returnKeyType?: string;
	testID?: string;
}

export interface PickerInputRef {
	openPicker: () => void;
}

// Create a type-safe generic component with forwardRef
function createPickerInput<T extends string>() {
	// Define the component as a render function
	const PickerInputComponent: ForwardRefRenderFunction<PickerInputRef, PickerInputProps<T>> = (
		{
			value,
			onChange,
			options,
			label,
			placeholder = 'Select an option',
			renderSelectedOption,
			onSubmitEditing,
			returnKeyType,
			testID,
		},
		ref,
	) => {
		const theme = useTheme();
		const [modalVisible, setModalVisible] = useState(false);
		const [showNativePicker, setShowNativePicker] = useState(false);
		const [isFocused, setIsFocused] = useState(false);
		const containerRef = useRef<ViewInstance>(null);

		// Expose methods via ref
		useImperativeHandle(ref, () => ({
			openPicker: () => {
				// Open the appropriate picker based on platform
				if (Platform.OS === 'web') {
					setModalVisible(true);
				} else {
					setShowNativePicker(true);
				}
			},
		}));

		// Handle keyboard navigation (web only)
		const handleKeyDown = useCallback((event: React.KeyboardEvent) => {
			const key = event.key;

			// Enter or Space opens the picker
			if (key === 'Enter' || key === ' ') {
				event.preventDefault();
				setModalVisible(true);
				return;
			}

			// Arrow keys cycle through options
			if (key === 'ArrowDown' || key === 'ArrowUp') {
				event.preventDefault();
				const currentIndex = options.findIndex(opt => opt.value === value);
				let newIndex: number;

				if (key === 'ArrowDown') {
					newIndex = currentIndex < options.length - 1 ? currentIndex + 1 : 0;
				} else {
					newIndex = currentIndex > 0 ? currentIndex - 1 : options.length - 1;
				}

				onChange(options[newIndex].value);
				return;
			}

			// Letter keys select first matching option
			if (key.length === 1 && /[a-zA-Z0-9]/.test(key)) {
				const lowerKey = key.toLowerCase();
				const matchingOption = options.find(opt =>
					opt.label.toLowerCase().startsWith(lowerKey),
				);

				if (matchingOption && matchingOption.value !== value) {
					onChange(matchingOption.value);
				}
				return;
			}

			// Tab moves to next field (default behavior, but trigger onSubmitEditing)
			if (key === 'Tab' && !event.shiftKey && onSubmitEditing) {
				// Let tab happen naturally, but also trigger submit
				setTimeout(onSubmitEditing, 0);
			}
		}, [options, value, onChange, onSubmitEditing]);

		// Find the selected option
		const selectedOption = options.find(option => option.value === value) || options[0];

		// Handle selection and submit
		const handleSelect = (newValue: T) => {
			onChange(newValue);
			if (onSubmitEditing) {
				// If we're using keyboard navigation, trigger the onSubmitEditing callback
				// after a short delay to allow the picker to close
				setTimeout(onSubmitEditing, 100);
			}
		};

		// Default renderer for selected option
		const defaultRenderSelectedOption = (option: PickerOption<T>) => (
			<View style={styles.selectedOptionContainer}>
				{option.icon && <Icon name={option.icon} size={20} />}
				<Text style={styles.selectedOptionText}>{option.label}</Text>
			</View>
		);

		// Web implementation with modal selection
		const renderWebPicker = () => (
			<>
				<Pressable
					ref={containerRef}
					style={[
						styles.pickerContainer,
						{ backgroundColor: theme.colors.surfaceVariant, borderRadius: 8 },
						isFocused && styles.pickerContainerFocused,
					]}
					onPress={() => setModalVisible(true)}
					onFocus={() => setIsFocused(true)}
					onBlur={() => setIsFocused(false)}
					// @ts-expect-error - web-specific props for keyboard accessibility
					onKeyDown={Platform.OS === 'web' ? handleKeyDown : undefined}
					tabIndex={Platform.OS === 'web' ? 0 : undefined}
					role="combobox"
					aria-expanded={modalVisible}
					aria-haspopup="listbox"
					testID={testID}
				>
					{selectedOption
						? (renderSelectedOption ? renderSelectedOption(selectedOption) : defaultRenderSelectedOption(selectedOption))
						: <Text style={styles.placeholder}>{placeholder}</Text>
					}
					<View pointerEvents="none" tabIndex={-1}>
						<Icon name="chevron-down" size={20} style={styles.dropdownIcon} />
					</View>
				</Pressable>

				<Portal>
					<Modal
						visible={modalVisible}
						onDismiss={() => setModalVisible(false)}
						contentContainerStyle={{
							backgroundColor: theme.colors.background,
							margin: 20,
							borderRadius: 10,
							padding: 0,
							maxWidth: 500,
							alignSelf: 'center',
							width: '90%',
						}}
					>
						<View style={styles.modalHeader}>
							<Text style={styles.modalTitle}>{label || 'Select an option'}</Text>
						</View>

						{options.map((option) => (
							<TouchableOpacity
								key={option.value as string}
								style={styles.optionItem}
								onPress={() => {
									handleSelect(option.value);
									setModalVisible(false);
								}}
							>
								{option.icon && <Icon name={option.icon} size={24} />}
								<Text style={styles.optionText}>{option.label}</Text>
							</TouchableOpacity>
						))}

						<View style={styles.modalFooter}>
							<Button onPress={() => setModalVisible(false)}>
								<Text>Cancel</Text>
							</Button>
						</View>
					</Modal>
				</Portal>
			</>
		);

		// Native implementation with native picker
		const renderNativePicker = () => {
			// If Picker is not available (e.g., on web), fall back to web picker
			if (!Picker) {
				return renderWebPicker();
			}

			return (
				<>
					{/* Display mode - shown when not interacting */}
					{!showNativePicker && (
						<TouchableOpacity
							style={[
								styles.pickerContainer,
								{ backgroundColor: theme.colors.surfaceVariant, borderRadius: 8 },
							]}
							onPress={() => setShowNativePicker(true)}
						>
							{selectedOption
								? (renderSelectedOption ? renderSelectedOption(selectedOption) : defaultRenderSelectedOption(selectedOption))
								: <Text style={styles.placeholder}>{placeholder}</Text>
							}
							<View pointerEvents="none">
								<Icon name="chevron-down" size={20} style={styles.dropdownIcon} />
							</View>
						</TouchableOpacity>
					)}

					{/* Edit mode - shown when picker is active */}
					{showNativePicker && (
						<View style={[styles.nativePickerWrapper, { backgroundColor: theme.colors.surfaceVariant }]}>
							<View style={styles.nativePickerHeader}>
								<Text style={styles.nativePickerTitle}>{label || 'Select an option'}</Text>
								<Button
									onPress={() => {
										setShowNativePicker(false);
										if (onSubmitEditing) {
											onSubmitEditing();
										}
									}}
									mode="text"
								>
									<Text>Done</Text>
								</Button>
							</View>
							<View
								style={[
									styles.pickerContainer,
									{
										backgroundColor: theme.colors.surfaceVariant,
										borderRadius: 8,
										overflow: 'hidden',
									},
								]}
							>
								<Picker
									selectedValue={value}
									onValueChange={(itemValue: T) => {
										handleSelect(itemValue as T);
										// Don't auto-close the picker on native; user must press Done
									}}
									style={styles.nativePicker}
								>
									{options.map((option) => (
										<Picker.Item
											key={option.value as string}
											label={option.label}
											value={option.value}
										/>
									))}
								</Picker>
							</View>
						</View>
					)}
				</>
			);
		};

		return (
			<View style={styles.container}>
				{label && !showNativePicker && <Text style={[styles.label, { marginBottom: 8 }]}>{label}</Text>}
				{Platform.OS === 'web' ? renderWebPicker() : renderNativePicker()}
			</View>
		);
	};

	// Apply forwardRef and return the component
	const ForwardedComponent = forwardRef(PickerInputComponent);
	ForwardedComponent.displayName = 'PickerInput';
	return ForwardedComponent;
}

// Export the picker input component
export const PickerInput = createPickerInput<string>();

const styles = StyleSheet.create({
	container: {
		marginVertical: 12,
	},
	label: {
		marginBottom: 6,
		fontSize: 14,
		fontWeight: '600',
		color: '#424242',
		textTransform: 'uppercase',
		letterSpacing: 0.5,
	},
	pickerContainer: {
		flexDirection: 'row',
		alignItems: 'center',
		padding: 16,
		minHeight: 56,
		borderRadius: 8,
		borderWidth: 1,
		borderColor: '#e0e0e0',
		backgroundColor: '#ffffff',
		boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
	},
	pickerContainerFocused: {
		borderColor: '#1976d2',
		borderWidth: 2,
		boxShadow: '0 0 0 3px rgba(25, 118, 210, 0.2)',
	},
	selectedOptionContainer: {
		flexDirection: 'row',
		alignItems: 'center',
		flex: 1,
	},
	selectedOptionText: {
		fontSize: 16,
		flex: 1,
		fontWeight: '500',
		color: '#212121',
	},
	placeholder: {
		fontSize: 16,
		color: '#757575',
		flex: 1,
		fontStyle: 'italic',
	},
	dropdownIcon: {
		marginLeft: 'auto',
	},
	modalHeader: {
		padding: 20,
		borderBottomWidth: 1,
		borderBottomColor: '#e0e0e0',
		backgroundColor: '#f5f5f5',
	},
	modalTitle: {
		fontSize: 18,
		fontWeight: '600',
		color: '#424242',
	},
	optionItem: {
		flexDirection: 'row',
		alignItems: 'center',
		padding: 16,
		borderBottomWidth: 1,
		borderBottomColor: '#e0e0e0',
		minHeight: 56,
		backgroundColor: '#ffffff',
	},
	optionText: {
		marginLeft: 12,
		fontSize: 16,
		fontWeight: '500',
		color: '#212121',
	},
	modalFooter: {
		padding: 16,
		alignItems: 'flex-end',
		backgroundColor: '#f5f5f5',
	},
	nativePicker: {
		width: '100%',
		backgroundColor: 'transparent',
	},
	nativePickerWrapper: {
		borderRadius: 8,
		overflow: 'hidden',
		width: '100%',
		backgroundColor: '#ffffff',
		borderWidth: 1,
		borderColor: '#e0e0e0',
	},
	nativePickerHeader: {
		flexDirection: 'row',
		justifyContent: 'space-between',
		alignItems: 'center',
		paddingHorizontal: 20,
		paddingVertical: 12,
		borderBottomWidth: 1,
		borderBottomColor: '#e0e0e0',
		backgroundColor: '#f5f5f5',
	},
	nativePickerTitle: {
		fontSize: 16,
		fontWeight: '600',
		color: '#424242',
	},
});
